import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { Lock, User, Building2, Eye, EyeOff, Loader2, ArrowLeft, ShieldAlert, Clock, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useBranchAuth } from '../context/BranchAuthContext';
import { useSettings } from '../context/SettingsContext';
import { resolveImageUrl } from '../components/ImageUpload';
import { useToast } from '../hooks/use-toast';

/**
 * BranchLogin — dedicated sign-in page for branch accounts.
 * Route: /branch/login  ·  Design brief (July 2026):
 *   - Mohammed Riaz official logo (pulled from Site Settings' logo_image)
 *   - Clean, minimal, professional dark theme (matches AdminLogin)
 *   - Username + password only — no public sign-up, no OAuth, no OTP
 *   - Post-login → /branch (My Orders)
 *
 * Feb 2026 — When the backend rejects login with `code: off_duty`,
 * we surface the message + shift window as a persistent inline
 * banner (titled "Off-duty") instead of a fleeting toast so the
 * user knows exactly why they're being blocked and when they can
 * try again.
 */
const BranchLogin = () => {
  const { token, login, loading, isSuperBypass } = useBranchAuth();
  const { settings } = useSettings();
  const logoUrl = resolveImageUrl(settings?.logo_image || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [offDuty, setOffDuty] = useState(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => { document.title = 'Branch Portal — Sign In'; }, []);

  // Super-admin bypass: an already-signed-in super admin never sees
  // the branch login page — they're redirected straight to the
  // full-fleet order view.
  if (isSuperBypass) return <Navigate to="/branch/orders" replace />;
  if (token) return <Navigate to="/branch" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast({ title: 'Enter username and password' });
      return;
    }
    // Clear any previous off-duty banner before retrying.
    setOffDuty(null);
    const res = await login(username.trim(), password);
    if (res.ok) {
      toast({ title: 'Welcome', description: res.mustChangePassword ? 'Please change your temporary password.' : 'Signed in to branch portal.' });
      navigate('/branch', { replace: true });
    } else if (res.offDuty) {
      // Persistent inline banner — no toast on off-duty rejections.
      setOffDuty(res.offDuty);
    } else {
      toast({ title: 'Login failed', description: res.error || 'Invalid username or password' });
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1220] flex items-center justify-center p-4 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '24px 24px' }}
      />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#0078D4]/20 blur-[120px]" />

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-6 transition"
          data-testid="branch-login-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to storefront
        </Link>

        <div className="bg-[#111827] border border-white/10 rounded-3xl shadow-2xl p-8 backdrop-blur">
          <div className="flex flex-col items-center gap-3 mb-7 text-center">
            {logoUrl ? (
              <div
                className="w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center overflow-hidden"
                data-testid="branch-login-logo"
              >
                <img
                  src={logoUrl}
                  alt="Mohammed Riaz & Partner LLC"
                  className="w-full h-full object-contain drop-shadow-[0_0_24px_rgba(0,120,212,0.35)]"
                />
              </div>
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-[#0078D4] flex items-center justify-center shadow-lg shadow-[#0078D4]/40">
                <Building2 className="w-10 h-10 text-white" />
              </div>
            )}
            <div>
              <div className="text-[10px] font-bold tracking-[0.25em] text-[#60A5FA] uppercase">
                Branch Portal
              </div>
              <h1 className="font-display text-2xl text-white leading-tight mt-1">Branch sign in</h1>
            </div>
          </div>

          <p className="text-sm text-slate-400 -mt-2 mb-6">
            Restricted area — authorized branch staff only. Contact your administrator if you need access.
          </p>

          {offDuty && (
            <div
              role="alert"
              className="mb-5 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex gap-3"
              data-testid="branch-login-off-duty-banner"
            >
              <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center flex-shrink-0">
                <ShieldAlert className="w-4 h-4 text-amber-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className="text-[11px] font-extrabold tracking-[0.2em] uppercase text-amber-300"
                  data-testid="branch-login-off-duty-title"
                >
                  {offDuty.reason === 'off_day' ? 'Off-duty · Scheduled off-day' : 'Off-duty'}
                </div>
                <p
                  className="text-sm text-amber-50 leading-snug mt-1"
                  data-testid="branch-login-off-duty-message"
                >
                  {offDuty.message}
                </p>
                {offDuty.reason === 'off_day' ? (
                  <div className="mt-3 rounded-lg bg-black/30 border border-white/5 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300 flex items-center gap-1">
                      <CalendarIcon className="w-3 h-3" /> Access resumes
                    </div>
                    <div
                      className="text-xs font-bold text-white mt-0.5"
                      data-testid="branch-login-off-duty-days"
                    >
                      On the next working day
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="rounded-lg bg-black/30 border border-white/5 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Shift window
                      </div>
                      <div
                        className="text-xs font-bold text-white mt-0.5 tabular-nums"
                        data-testid="branch-login-off-duty-window"
                      >
                        {offDuty.shift_window_display}
                      </div>
                    </div>
                    <div className="rounded-lg bg-black/30 border border-white/5 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300 flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" /> Duty days
                      </div>
                      <div
                        className="text-xs font-bold text-white mt-0.5"
                        data-testid="branch-login-off-duty-days"
                      >
                        {offDuty.duty_days_display}
                      </div>
                    </div>
                  </div>
                )}
                <div className="text-[10px] text-amber-200/70 mt-2">
                  All times shown in office local time (Muscat · UTC+4).
                </div>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="branch-login-form">
            <div>
              <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider" htmlFor="branch-username">Username</label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="branch-username"
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. barka"
                  autoComplete="username"
                  className="pl-10 bg-[#0B1220] border-white/10 text-white placeholder:text-slate-500 focus:border-[#0078D4]"
                  data-testid="branch-login-username"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider" htmlFor="branch-password">Password</label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="branch-password"
                  type={showPwd ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="pl-10 pr-10 bg-[#0B1220] border-white/10 text-white placeholder:text-slate-500 focus:border-[#0078D4]"
                  data-testid="branch-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold rounded-xl mt-2"
              data-testid="branch-login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
            </Button>
          </form>

          <p className="text-[11px] text-slate-500 text-center mt-6">
            Trouble signing in? Contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BranchLogin;
